import { Fragment, useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import ProviderSearchSelect from "@/components/ProviderSearchSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, ExternalLink, Plus, Mail, Trash2, Hotel, Car, MapPin, Mails, MessageSquare, RefreshCw } from "lucide-react";
import ReplyCard from "@/components/ReplyCard";
import { AttachmentChips } from "@/components/Attachments";
import { useAuth } from "@/context/AuthContext";
import EmailComposer from "@/components/EmailComposer";
import { Checkbox } from "@/components/ui/checkbox";

const SERVICE_ICONS = { hotel: Hotel, transfer: Car, actividad: MapPin };
const SERVICE_LABEL = { hotel: "Hotel", transfer: "Transfer", actividad: "Actividad" };
const SOFI_STATUS_COLORS = {
  contacto: "bg-amber-50 text-amber-700",
  reserva: "bg-blue-50 text-blue-700",
  confirmacion: "bg-emerald-50 text-emerald-700",
  cancelada: "bg-red-50 text-red-700",
};

/** Semáforo por reserva: rojo = discrepancia, verde = confirmada, ámbar = esperando/por revisar. */
function bookingLight(b, thread) {
  const pend = thread.replies.filter((r) => r.status === "pending");
  if (pend.some((r) => (r.extraction?.discrepancias || []).length > 0))
    return { cls: "bg-red-500 ring-red-200", label: "Discrepancia del proveedor — revisar respuesta" };
  if (b.status === "confirmed")
    return { cls: "bg-emerald-500 ring-emerald-200", label: "Confirmada por el proveedor" };
  if (pend.length > 0)
    return { cls: "bg-amber-400 ring-amber-200 animate-pulse", label: "Respuesta recibida — pendiente de revisar" };
  if (b.status === "sent")
    return { cls: "bg-amber-400 ring-amber-200", label: "Correo enviado — esperando respuesta del proveedor" };
  if (b.status === "cancelled")
    return { cls: "bg-gray-400 ring-gray-200", label: "Cancelada" };
  return { cls: "bg-gray-300 ring-gray-100", label: "Sin contactar" };
}

function emptyBooking() {
  return {
    date: "",
    time: "",
    service_type: "actividad",
    description: "",
    provider_id: "",
    pax_adults: "",
    pax_children: "",
    origin: "",
    destination: "",
    meeting_point: "",
    notes: "",
  };
}

export default function TripDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const etapa2 = !!user?.features?.etapa2;
  const [trip, setTrip] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [providers, setProviders] = useState([]);
  const [bForm, setBForm] = useState(emptyBooking());
  const [openCreate, setOpenCreate] = useState(false);
  const [composerData, setComposerData] = useState(null); // { booking, provider } | { bookings, provider, multi: true }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [emailsData, setEmailsData] = useState({ sent: [], replies: [] });
  const [expandedThread, setExpandedThread] = useState(null);
  const [syncingFinance, setSyncingFinance] = useState(false);

  const fetchAll = async () => {
    const [t, b, p] = await Promise.all([
      api.get(`/trips/${id}`),
      api.get(`/trips/${id}/bookings`),
      api.get(`/providers`),
    ]);
    setTrip(t.data);
    setBookings(b.data);
    setProviders(p.data);
    api.get(`/trips/${id}/emails`).then(({ data }) => setEmailsData(data)).catch(() => {});
  };

  useEffect(() => {
    fetchAll();
  }, [id]);

  const providerById = useMemo(() => {
    const m = {};
    providers.forEach((p) => (m[p.id] = p));
    return m;
  }, [providers]);

  const onCreateBooking = async (e) => {
    e.preventDefault();
    const payload = {
      ...bForm,
      pax_adults: bForm.pax_adults ? Number(bForm.pax_adults) : null,
      pax_children: bForm.pax_children ? Number(bForm.pax_children) : null,
      provider_id: bForm.provider_id || null,
    };
    try {
      await api.post(`/trips/${id}/bookings`, payload);
      toast.success("Reserva añadida");
      setBForm(emptyBooking());
      setOpenCreate(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const onDeleteBooking = async (bid) => {
    if (!confirm("¿Borrar esta línea de reserva?")) return;
    await api.delete(`/bookings/${bid}`);
    fetchAll();
  };

  const onChangeProvider = async (bid, providerId) => {
    await api.patch(`/bookings/${bid}`, { provider_id: providerId || null });
    fetchAll();
  };

  const toggleSelect = (booking) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(booking.id)) next.delete(booking.id);
      else next.add(booking.id);
      return next;
    });
  };

  const emailsForBooking = (b) => {
    const sent = emailsData.sent.filter(
      (e) => e.booking_line_id === b.id || (!!e.provider_id && e.provider_id === b.provider_id)
    );
    const replies = emailsData.replies.filter(
      (r) => (r.matched?.booking_ids || []).includes(b.id) ||
             (!!r.matched?.provider_id && r.matched.provider_id === b.provider_id)
    );
    return { sent, replies };
  };

  const onSyncFinance = async () => {
    setSyncingFinance(true);
    try {
      const { data } = await api.post(`/trips/${id}/refresh-finance`);
      toast.info(`Actualizando ${data.count} reservas desde Sofi en segundo plano…`);
      // los datos van llegando: refrescamos en diferido
      setTimeout(() => { fetchAll(); }, 25000);
      setTimeout(() => { fetchAll(); setSyncingFinance(false); toast.success("Datos de Sofi actualizados"); }, 80000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar desde Sofi");
      setSyncingFinance(false);
    }
  };

  const canSendMultiSelection = () => {
    if (selectedIds.size < 1) return null;
    const selected = bookings.filter((b) => selectedIds.has(b.id));
    const providerIds = new Set(selected.map((b) => b.provider_id).filter(Boolean));
    if (providerIds.size !== 1) return { ok: false, reason: "Las reservas seleccionadas tienen proveedores distintos" };
    const provider = providerById[[...providerIds][0]];
    if (!provider) return { ok: false, reason: "Proveedor no encontrado" };
    if (provider.sistema_reserva !== "MAIL") return { ok: false, reason: "El proveedor no es de tipo MAIL" };
    if (!provider.email) return { ok: false, reason: "El proveedor no tiene email" };
    return { ok: true, provider, bookings: selected };
  };

  const onSendMulti = () => {
    const check = canSendMultiSelection();
    if (!check) return;
    if (!check.ok) { toast.error(check.reason); return; }
    setComposerData({ multi: true, bookings: check.bookings, provider: check.provider, trip });
  };

  if (!trip) return <div className="text-sm text-gray-500">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="trip-detail-page">
      <div>
        <Link to="/itinerarios" className="inline-flex items-center text-xs text-gray-500 hover:text-gray-900">
          <ArrowLeft size={14} className="mr-1" /> Volver a itinerarios
        </Link>
        <div className="flex items-start justify-between mt-2 gap-4 flex-wrap">
          <div>
            <p className="vv-label">Itinerario · {trip.reference}</p>
            <h1 className="text-3xl font-semibold tracking-tight mt-1">{trip.client_name || <span className="text-gray-400 italic font-normal">Pendiente de importar</span>}</h1>
          </div>
          {trip.external_url && (
            <a href={trip.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs text-blue-600 hover:underline">
              Ver en gestion <ExternalLink size={12} className="ml-1" />
            </a>
          )}
        </div>
      </div>

      <Tabs defaultValue="reservas" className="w-full">
        <TabsList className="bg-gray-100">
          <TabsTrigger value="resumen" data-testid="tab-resumen">Resumen</TabsTrigger>
          <TabsTrigger value="reservas" data-testid="tab-reservas">Reservas ({bookings.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="resumen" className="mt-4">
          <div className="vv-card p-6 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5">
            <Field label="Cliente" value={trip.client_name} />
            <Field label="Email" value={trip.client_email} />
            <Field label="Teléfono" value={trip.client_phone} />
            <Field label="Pax" value={`${trip.adults} adultos${trip.children ? ` + ${trip.children} niños` : ""}`} />
            <Field label="Llegada" value={trip.arrival_date} />
            <Field label="Salida" value={trip.departure_date} />
            <Field label="Vuelo" value={trip.flight_number} />
            <Field label="Estado" value={trip.status} />
            {trip.notes && (
              <div className="col-span-full">
                <p className="vv-label mb-1">Notas</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{trip.notes}</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="reservas" className="mt-4">
          <div className="vv-card overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--vv-border)] gap-3 flex-wrap">
              <div>
                <h3 className="text-sm font-semibold">Reservas día a día</h3>
                <p className="text-xs text-gray-500">Los proveedores con sistema <span className="font-mono">MAIL</span> muestran botón de envío. Marca varias para enviarlas en un solo correo al mismo proveedor.</p>
              </div>
              <div className="flex gap-2 items-center">
                <Button size="sm" variant="outline" onClick={onSyncFinance} disabled={syncingFinance} className="h-8 text-xs" data-testid="sync-finance-button" title="Trae precio, estado y factura de cada reserva desde gestion">
                  <RefreshCw size={13} className={`mr-1 ${syncingFinance ? "animate-spin" : ""}`} /> Actualizar desde Sofi
                </Button>
                {selectedIds.size > 0 && (
                  <>
                    <span className="text-xs text-gray-500">{selectedIds.size} seleccionada{selectedIds.size > 1 ? "s" : ""}</span>
                    <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())} className="h-8 text-xs">Limpiar</Button>
                    <Button size="sm" onClick={onSendMulti} className="bg-blue-600 hover:bg-blue-700 h-8" data-testid="send-multi-button">
                      <Mails size={14} className="mr-1"/> Enviar correo conjunto ({selectedIds.size})
                    </Button>
                  </>
                )}
                <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-booking-button">
                      <Plus size={14} className="mr-1" /> Añadir reserva
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader><DialogTitle>Nueva reserva</DialogTitle></DialogHeader>
                  <form onSubmit={onCreateBooking} className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="vv-label">Fecha *</Label>
                      <Input type="date" required value={bForm.date} onChange={(e) => setBForm({ ...bForm, date: e.target.value })} data-testid="booking-date-input"/>
                    </div>
                    <div>
                      <Label className="vv-label">Hora</Label>
                      <Input type="time" value={bForm.time} onChange={(e) => setBForm({ ...bForm, time: e.target.value })} />
                    </div>
                    <div>
                      <Label className="vv-label">Tipo *</Label>
                      <Select value={bForm.service_type} onValueChange={(v) => setBForm({ ...bForm, service_type: v })}>
                        <SelectTrigger data-testid="booking-type-select"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="actividad">Actividad</SelectItem>
                          <SelectItem value="hotel">Hotel</SelectItem>
                          <SelectItem value="transfer">Transfer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="vv-label">Proveedor</Label>
                      <ProviderSearchSelect
                        value={bForm.provider_id}
                        onChange={(v) => setBForm({ ...bForm, provider_id: v })}
                        providers={providers}
                        preferType={bForm.service_type}
                        testId="booking-provider-select"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="vv-label">Descripción del servicio *</Label>
                      <Input required value={bForm.description} onChange={(e) => setBForm({ ...bForm, description: e.target.value })} data-testid="booking-description-input" placeholder="Visita al Parque Güell con guía"/>
                    </div>
                    <div>
                      <Label className="vv-label">Adultos</Label>
                      <Input type="number" min={0} value={bForm.pax_adults} onChange={(e) => setBForm({ ...bForm, pax_adults: e.target.value })} />
                    </div>
                    <div>
                      <Label className="vv-label">Niños</Label>
                      <Input type="number" min={0} value={bForm.pax_children} onChange={(e) => setBForm({ ...bForm, pax_children: e.target.value })} />
                    </div>
                    <div>
                      <Label className="vv-label">Origen / Pick-up</Label>
                      <Input value={bForm.origin} onChange={(e) => setBForm({ ...bForm, origin: e.target.value })} />
                    </div>
                    <div>
                      <Label className="vv-label">Destino / Drop-off</Label>
                      <Input value={bForm.destination} onChange={(e) => setBForm({ ...bForm, destination: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="vv-label">Meeting point</Label>
                      <Input value={bForm.meeting_point} onChange={(e) => setBForm({ ...bForm, meeting_point: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="vv-label">Notas</Label>
                      <Textarea rows={2} value={bForm.notes} onChange={(e) => setBForm({ ...bForm, notes: e.target.value })} />
                    </div>
                    <DialogFooter className="col-span-2">
                      <Button type="button" variant="ghost" onClick={() => setOpenCreate(false)}>Cancelar</Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-booking-button">Guardar</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            {bookings.length === 0 ? (
              <div className="p-12 text-center text-sm text-gray-500">Sin reservas todavía. Añade la primera línea.</div>
            ) : (
              <table className="vv-table w-full">
                <thead>
                  <tr>
                    <th className="w-8"></th>
                    <th className="w-6" title="Estado: verde = confirmada · ámbar = esperando · rojo = discrepancia"></th>
                    <th className="w-24">Fecha</th>
                    <th className="w-16">Hora</th>
                    <th className="w-24">Tipo</th>
                    <th>Servicio</th>
                    <th>Proveedor</th>
                    <th>Sistema</th>
                    <th className="w-24">Sofi</th>
                    <th className="w-20">Correos</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b, i) => {
                    const provider = b.provider_id ? providerById[b.provider_id] : null;
                    const Icon = SERVICE_ICONS[b.service_type];
                    const isMailReady = provider?.sistema_reserva === "MAIL" && provider?.email;
                    const checked = selectedIds.has(b.id);
                    const thread = emailsForBooking(b);
                    const nThread = thread.sent.length + thread.replies.length;
                    const pendingReplies = thread.replies.filter((r) => r.status === "pending").length;
                    const light = bookingLight(b, thread);
                    return (
                      <Fragment key={b.id}>
                      <tr className={`vv-row-${b.status}`} data-testid={`booking-row-${i}`}>
                        <td>
                          <Checkbox
                            checked={checked}
                            disabled={!isMailReady}
                            onCheckedChange={() => toggleSelect(b)}
                            aria-label={`Seleccionar reserva ${i + 1}`}
                            data-testid={`select-booking-${i}`}
                          />
                        </td>
                        <td>
                          <span
                            className={`inline-block w-2.5 h-2.5 rounded-full ring-2 ${light.cls}`}
                            title={light.label}
                            data-testid={`booking-light-${i}`}
                          />
                        </td>
                        <td className="mono text-xs">{b.date}</td>
                        <td className="mono text-xs">{b.time || "—"}</td>
                        <td>
                          <span className="inline-flex items-center gap-1 text-xs"><Icon size={13} /> {SERVICE_LABEL[b.service_type]}</span>
                        </td>
                        <td className="font-medium">{b.description}</td>
                        <td className="min-w-[220px]">
                          <ProviderSearchSelect
                            value={b.provider_id || ""}
                            onChange={(v) => onChangeProvider(b.id, v)}
                            providers={providers}
                            preferType={b.service_type}
                            size="sm"
                            testId={`booking-provider-${i}`}
                          />
                        </td>
                        <td>
                          {provider ? (
                            <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${provider.sistema_reserva === "MAIL" ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                              {provider.sistema_reserva}
                            </span>
                          ) : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="whitespace-nowrap">
                          <div className="flex flex-col gap-0.5">
                            {b.price != null && <span className="text-[11px] font-medium text-gray-800">{b.price} €</span>}
                            <span className="flex items-center gap-1">
                              {b.sofi_status && b.sofi_status !== "sin" ? (
                                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${SOFI_STATUS_COLORS[b.sofi_status] || "bg-gray-100 text-gray-600"}`}>{b.sofi_status}</span>
                              ) : (
                                <span className="text-[9px] uppercase tracking-wider text-gray-400">sin estado</span>
                              )}
                              {b.factura_solicitada && <span className="text-[9px] bg-teal-50 text-teal-700 border border-teal-200 rounded px-1 py-px">FAC</span>}
                            </span>
                          </div>
                        </td>
                        <td>
                          {nThread > 0 ? (
                            <button
                              onClick={() => setExpandedThread(expandedThread === b.id ? null : b.id)}
                              className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 border transition-colors ${pendingReplies > 0 ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" : "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"}`}
                              data-testid={`thread-toggle-${i}`}
                              title="Ver correos de esta reserva"
                            >
                              <MessageSquare size={11} /> {nThread}
                              {pendingReplies > 0 && <span className="font-semibold">· {pendingReplies}!</span>}
                            </button>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="text-right whitespace-nowrap">
                          {provider?.sistema_reserva === "MAIL" && provider?.email ? (
                            <Button size="sm" variant="outline" className="h-7 text-xs mr-2 border-blue-600 text-blue-700 hover:bg-blue-50"
                              onClick={() => setComposerData({ booking: b, provider, trip })}
                              data-testid={`send-mail-button-${i}`}>
                              <Mail size={12} className="mr-1" /> Enviar mail
                            </Button>
                          ) : provider && provider.sistema_reserva !== "MAIL" ? (
                            <span className="text-[11px] text-gray-400 mr-2">No es Mail</span>
                          ) : null}
                          <button onClick={() => onDeleteBooking(b.id)} className="text-gray-400 hover:text-red-600 inline-flex" title="Borrar">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                      {expandedThread === b.id && (
                        <tr key={`${b.id}-thread`}>
                          <td colSpan={11} className="bg-gray-50/70 p-0">
                            <BookingThread booking={b} sent={thread.sent} replies={thread.replies} onAction={() => fetchAll()} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {composerData && (
        <EmailComposer
          open={!!composerData}
          onClose={(refresh) => {
            setComposerData(null);
            if (refresh) {
              setSelectedIds(new Set());
              fetchAll();
            }
          }}
          trip={composerData.trip}
          booking={composerData.booking}
          bookings={composerData.bookings || []}
          multi={!!composerData.multi}
          provider={composerData.provider}
        />
      )}
    </div>
  );
}

function BookingThread({ booking, sent, replies, onAction }) {
  const [expandedMsg, setExpandedMsg] = useState(null);
  const items = [
    ...sent.map((e) => ({ kind: "sent", date: e.created_at, e })),
    ...replies.map((r) => ({ kind: "reply", date: r.date || r.created_at, r })),
  ].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  return (
    <div className="px-6 py-3 space-y-2" data-testid={`booking-thread-${booking.id}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500">
        <MessageSquare size={12} /> Correos de esta reserva
        {booking.price != null && (
          <span className="normal-case tracking-normal bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-1.5 py-0.5">Precio Sofi: {booking.price} €</span>
        )}
      </div>
      {items.length === 0 && <p className="text-xs text-gray-400">Sin correos todavía.</p>}
      {items.map((it) => {
        if (it.kind === "sent") {
          const e = it.e;
          const key = `s-${e.id}`;
          return (
            <div key={key} className="rounded-lg border border-blue-200 bg-blue-50/40 px-3 py-2 text-xs ml-10">
              <button className="w-full text-left" onClick={() => setExpandedMsg(expandedMsg === key ? null : key)}>
                <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500">
                  <span className="font-medium text-blue-700 uppercase">→ Enviado</span>
                  <span>{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  <span className="truncate">a {e.to_email}</span>
                  {e.status === "failed" && <span className="text-red-600 font-medium" title={e.error || ""}>FALLÓ</span>}
                  {e.status === "queued" && <span className="text-amber-600 font-medium">ENVIANDO…</span>}
                </div>
                <div className="font-medium text-gray-800 mt-0.5">{e.subject}</div>
              </button>
              {expandedMsg === key && (
                <pre className="whitespace-pre-wrap font-sans text-gray-700 mt-1.5 max-h-48 overflow-y-auto border-t border-blue-100 pt-1.5">{e.body}</pre>
              )}
              {(e.attachments || []).length > 0 && (
                <div className="mt-1.5">
                  <AttachmentChips attachments={e.attachments} testIdPrefix={`sent-att-${e.id}`} />
                </div>
              )}
            </div>
          );
        }
        const r = it.r;
        return (
          <div key={`r-${r.id}`} className="mr-6">
            <ReplyCard
              reply={r}
              tab={r.status === "pending" ? "pending" : r.status}
              onReviewed={onAction}
            />
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="vv-label">{label}</p>
      <p className="text-sm font-medium text-gray-900 mt-1">{value || <span className="text-gray-400">—</span>}</p>
    </div>
  );
}
